/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * "Giá & dịch vụ" of a TLD (owner, 2026-09-29: "sao không gộp ở 1 chỗ"): the only place where a
 * TLD's prices and add-ons are configured, top to bottom —
 *   a. what each operation costs the customer for one year (engine breakdown, line by line),
 *   b. the registry cost of the TLD,
 *   c. fees & add-ons on at this TLD: mode, cost / pass-through fee, selling price, one price
 *      dialog; add-ons off folded away; the .vn name-change operation fees on their own,
 *   d. selling prices: this TLD's row of every price book (cells open the price dialog).
 * The add-on catalogue is read-only here (Dịch vụ tên miền hub); the sections below the summary
 * are the advanced per-section editing, collapsed under "Tuỳ chỉnh nâng cao".
 */
import { ListChecks, Pencil, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { PriceBook, RegistryPrice } from '../../api/areas/commerce';
import type { AddonMode, OperationBreakdown, TldAddon, TldAddonChange } from '../../api/areas/services';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader } from '../../components/PageHeader';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import type { AddonPricesInput } from '../../api/areas/operator';
import { decimalInput, GRID_DIALOG, GridCell, GridHeader, GridRow, isDecimal, roundMoney, rowErrors } from '../commerce/PriceGrid';
import { Amount } from '../commerce/shared';
import { priceOf, shortDate, TldBookMatrix, useRuleWords } from '../commerce/PriceMatrix';
import { EndPriceWindowButton, RegistryPriceButton } from '../operator/Pricing';
import { ActionDialog, FormFooter, ReasonAction, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';
import { useEnum, useLocalName } from '../services/shared';

interface TldRef {
  id: string;
  zone: string;
  tld_class: string;
}

type Lookup = (key: string, options?: Record<string, unknown>) => string;
type ModeChoice = AddonMode | 'OFF';
const MODES: ModeChoice[] = ['MANDATORY', 'DEFAULT', 'OPTIONAL'];
const live = (x: { effective_until: string | null }) => x.effective_until === null || x.effective_until > new Date().toISOString();

// ---------------------------------------------------------------------------
// a. Price per operation

function OperationCard({ op, currency, missing }: { op: OperationBreakdown; currency: string; missing: string[] }) {
  const { t, i18n } = useTranslation('services');
  const e = useEnum();
  const desc = (l: { description_vi: string; description_en: string }) => (i18n.language === 'vi' ? l.description_vi : l.description_en);
  return (
    <div className="flex min-w-0 flex-col rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
        <span className="font-medium">{e('operation', op.operation)}</span>
        {op.error ? <Badge tone="crit">{t('tldPricing.cannotPrice')}</Badge> : null}
      </div>
      {op.error ? (
        <p className="px-4 py-3 text-sm text-muted-foreground">{op.error.replace(/^no price available: /, '')}</p>
      ) : (
        <dl className="grid min-w-0 gap-1 px-4 py-3 text-sm">
          {op.registry_cost !== undefined && op.registry_cost !== null ? (
            <div className="flex min-w-0 items-baseline justify-between gap-2 text-xs text-muted-foreground" title={t('tldPricing.registryCostNote')}>
              <dt>{t('tldPricing.registryCost')}</dt>
              <dd className="shrink-0">
                <Amount amount={op.registry_cost} currency={currency} />
              </dd>
            </div>
          ) : null}
          {op.lines.map((l, i) => (
            <div key={`${l.code}-${i}`} className="flex min-w-0 items-baseline justify-between gap-2">
              <dt className="min-w-0">
                <span className="block truncate" title={desc(l)}>
                  {l.kind === 'SERVICE' ? e('lineKind', 'SERVICE') : desc(l)}
                </span>
                <span className="text-xs text-muted-foreground">{l.tax_category === 'KCT' ? 'KCT' : `VAT ${l.tax_rate ? `${Math.round(Number(l.tax_rate) * 100)} %` : ''}`}</span>
              </dt>
              <dd className="shrink-0">
                <Amount amount={l.amount} currency={currency} />
              </dd>
            </div>
          ))}
          <div className="flex min-w-0 items-baseline justify-between gap-2 text-xs text-muted-foreground">
            <dt>{t('tldPricing.tax')}</dt>
            <dd className="shrink-0">
              <Amount amount={op.tax} currency={currency} />
            </dd>
          </div>
          <div className="mt-1 flex items-baseline justify-between gap-2 border-t pt-2 font-semibold">
            <dt>{t('tldPricing.total')}</dt>
            <dd className="shrink-0">
              <Amount amount={op.total} currency={currency} />
            </dd>
          </div>
        </dl>
      )}
      {missing.length ? (
        <div className="mt-auto border-t px-4 py-2">
          {missing.map((m) => (
            <Badge key={m} tone="warn" className="mr-1">
              {t('tldPricing.feeMissing', { name: m })}
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function OperationSummary({ tld, books, addons, refreshKey }: { tld: TldRef; books: PriceBook[]; addons: TldAddon[]; refreshKey: number }) {
  const { t } = useTranslation('services');
  const name = useLocalName();
  const active = books.filter((b) => b.status === 'ACTIVE');
  const [book, setBook] = useState('');
  const result = useApi((signal) => api.getTldPriceBreakdown(tld.id, { price_book_id: book || undefined, years: 1 }, { signal }), [tld.id, book, refreshKey]);
  const chosen = active.find((b) => b.id === (book || (result.status === 'success' ? result.data.price_book_id : '')));
  const missingFor = (op: string) => addons.filter((a) => a.missing_price && a.mode === 'MANDATORY' && a.operations.includes(op as TldAddon['operations'][number])).map((a) => name(a));
  return (
    <Section title={t('tldPricing.summary')} description={t('tldPricing.summaryHint', { book: chosen?.code ?? '—' })}>
      {active.length > 1 ? (
        <label className="mb-3 flex max-w-sm items-center gap-2 text-sm">
          <span className="shrink-0 text-muted-foreground">{t('tldPricing.book')}</span>
          <select className={selectClass} value={book || chosen?.id || ''} onChange={(ev) => setBook(ev.target.value)}>
            {active.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {result.status === 'loading' ? <Loading rows={2} /> : null}
      {result.status === 'error' ? <DataError error={result.error} title={t('common.loadFailed')} onRetry={result.reload} /> : null}
      {result.status === 'success' ? (
        <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-4">
          {result.data.operations.map((op) => (
            <OperationCard key={op.operation} op={op} currency={op.currency} missing={missingFor(op.operation)} />
          ))}
        </div>
      ) : null}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// b. Registry cost

function RegistryCosts({ tld, prices, onChanged }: { tld: TldRef; prices: RegistryPrice[]; onChanged: () => void }) {
  const { t, i18n } = useTranslation('services');
  const e = useEnum();
  const rows = prices.filter((p) => p.tld_id === tld.id && p.operation !== 'ADDON' && live(p));
  const cols: Column<RegistryPrice>[] = [
    {
      key: 'op',
      header: t('tldAddons.col.ops'),
      cell: (p) => e('operation', p.operation === 'REDEMPTION' ? 'RESTORE' : p.operation),
    },
    {
      key: 'amount',
      header: t('tldPricing.costs'),
      numeric: true,
      cell: (p) => (
        <span className="inline-flex flex-col items-end">
          <Amount amount={p.amount} currency={p.currency} />
          {p.years !== 1 ? <span className="text-xs text-muted-foreground">{t('common.years', { count: p.years })}</span> : null}
        </span>
      ),
    },
    {
      key: 'from',
      header: '',
      className: 'text-xs text-muted-foreground',
      cell: (p) => (
        <span title={formatDateTime(p.effective_from)}>
          {p.effective_from > new Date().toISOString() ? '⏱ ' : ''}
          {t('tldPricing.from', {
            date: shortDate(p.effective_from, i18n.language),
          })}
        </span>
      ),
    },
    {
      key: 'end',
      header: '',
      className: 'text-right',
      cell: (p) => <EndPriceWindowButton id={p.id} kind="registry" open={!p.effective_until} title={`.${tld.zone} · ${e('operation', p.operation)}`} onChanged={onChanged} />,
    },
  ];
  return (
    <Section title={t('tldPricing.costs')} description={t('tldPricing.costsHint')} flush>
      <div className="mb-3 px-5">
        <RegistryPriceButton tldId={tld.id} onChanged={onChanged} />
      </div>
      {rows.length ? (
        <DataTable columns={cols} rows={rows} rowKey={(p) => p.id} />
      ) : (
        <Callout tone="warn" className="mx-5 mb-5">
          {t('tldPricing.noCosts')}
        </Callout>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// c. Fees & add-ons

function ModeForm({ close, changed, tld, addon, mode }: DialogBodyProps & { tld: TldRef; addon: TldAddon; mode: ModeChoice }) {
  const { t } = useTranslation('services');
  const tr = t as unknown as Lookup;
  const [reason, setReason] = useState('');
  const w = useWrite((_: null, opts) => api.setTldAddonMode(tld.id, addon.code, { mode: mode === 'OFF' ? null : mode, reason: reason.trim() }, opts));
  const done = w.state.phase === 'done';
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      <Callout tone="info" title={tr('tldPricing.modeTo', { mode: tr(`tldPricing.mode.${mode}`) })}>
        {tr(`tldPricing.modeHelp.${mode}`)}
      </Callout>
      {mode === 'MANDATORY' && !addon.free && addon.registry_costs.length + addon.rules.length === 0 ? <Callout tone="warn">{t('tldAddons.missingPrice')}</Callout> : null}
      {done ? <Callout tone="ok">{t('common.done')}</Callout> : <ReasonField value={reason} onChange={setReason} />}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={done ? undefined : t('common.save')} onClose={close} />
    </form>
  );
}

/** Bắt buộc / Tự thêm / Tuỳ chọn · Tắt: a click opens the reason dialog for that mode. */
function ModeControl({ addon, onPick, choices = [...MODES, 'OFF'], labelOf }: { addon: TldAddon; onPick: (m: ModeChoice) => void; choices?: ModeChoice[]; labelOf?: (m: ModeChoice) => string }) {
  const { t } = useTranslation('services');
  const tr = t as unknown as Lookup;
  return (
    <div className="flex items-center gap-1">
      <div className="inline-flex rounded-md border p-0.5" role="radiogroup" aria-label={t('tldPricing.col.mode')}>
        {choices.map((m) => {
          const on = (addon.mode ?? 'OFF') === m;
          return (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={on}
              title={tr(`tldPricing.modeHelp.${m}`)}
              onClick={() => !on && onPick(m)}
              className={`rounded px-2 py-1 text-xs whitespace-nowrap ${m === 'OFF' ? 'ml-0.5 border-l' : ''} ${on ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
            >
              {labelOf ? labelOf(m) : tr(`tldPricing.mode.${m}`)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CostCell({ a }: { a: TldAddon }) {
  const { t } = useTranslation('services');
  const e = useEnum();
  if (a.registry_costs.length)
    return (
      <ul className="grid gap-0.5 text-sm">
        {a.registry_costs.map((c) => (
          <li key={c.id} className="whitespace-nowrap">
            {c.amount_basis === 'PERCENT_OF_REGISTRY_COST' ? t('tldPrices.percent', { value: c.amount }) : <Amount amount={c.amount} currency={c.currency} />}
            {c.for_operation ? <span className="ml-1 text-xs text-muted-foreground">{t('tldPrices.forOp', { op: e('operation', c.for_operation) })}</span> : null}
          </li>
        ))}
      </ul>
    );
  if (a.free) return <Badge tone="ok">{t('tldPricing.free')}</Badge>;
  return a.missing_price ? <Badge tone="crit">{t('tldPricing.missing')}</Badge> : <span className="text-muted-foreground">—</span>;
}

function SellCell({ a }: { a: TldAddon }) {
  const { t } = useTranslation('services');
  const words = useRuleWords();
  if (a.rules.length)
    return (
      <ul className="grid gap-0.5 text-sm">
        {a.rules.map((r) => (
          <li key={r.id} className="whitespace-nowrap">
            <span className="mr-1 font-mono text-xs text-muted-foreground">{r.price_book}</span>
            {words(r, r.currency)}
          </li>
        ))}
      </ul>
    );
  return <span className="text-xs text-muted-foreground">{a.registry_costs.length ? t('tldPricing.atCost') : '—'}</span>;
}

type CostMethod = 'KEEP' | 'FIXED' | 'PERCENT_OF_REGISTRY_COST' | 'END';
type SellMethod = 'KEEP' | 'FIXED_PRICE' | 'MARKUP_PERCENT' | 'MARKUP_FIXED' | 'END';
const COST_ROWS = 'sm:grid-cols-[7rem_minmax(0,1.2fr)_minmax(0,1fr)_7rem_5rem]';
const SELL_ROWS = 'sm:grid-cols-[8rem_minmax(0,1fr)_minmax(0,1fr)_7rem_minmax(0,10rem)]';

/**
 * "Sửa giá": the add-on's cost for every operation it is charged with and its selling price in
 * every price book, one grid each, saved together (`POST /registry/tlds/{id}/addons/{code}/prices`).
 */
function PriceForm({ close, changed, tld, addon, books }: DialogBodyProps & { tld: TldRef; addon: TldAddon; books: PriceBook[] }) {
  const { t } = useTranslation('services');
  const tr = t as unknown as Lookup;
  const e = useEnum();
  const words = useRuleWords();
  const usable = books.filter((b) => b.status !== 'RETIRED');
  const opFee = addon.kind === 'OPERATION_FEE';
  // "Every operation" first, then one row per operation when there is more than one.
  const costKeys: (string | null)[] = [null, ...(addon.operations.length > 1 ? addon.operations : [])];
  const openCost = (op: string | null) => addon.registry_costs.find((c) => (c.for_operation ?? null) === op && live(c));
  const openRule = (bookId: string) => addon.rules.find((r) => r.price_book_id === bookId && live(r));
  const [costs, setCosts] = useState<Record<string, { method: CostMethod; amount: string; currency: string }>>({});
  const [sells, setSells] = useState<Record<string, { method: SellMethod; value: string }>>({});
  const [from, setFrom] = useState('');
  const [reason, setReason] = useState('');
  const [sent, setSent] = useState<string[]>([]);
  const w = useWrite((b: AddonPricesInput, opts) => api.publishTldAddonPrices(tld.id, addon.code, b, opts));
  const done = w.state.phase === 'done';
  const ck = (op: string | null) => `cost:${op ?? '*'}`;
  const cost = (op: string | null) => costs[ck(op)] ?? { method: 'KEEP' as CostMethod, amount: '', currency: openCost(op)?.currency ?? openCost(null)?.currency ?? 'VND' };
  const sell = (bookId: string) => sells[bookId] ?? { method: 'KEEP' as SellMethod, value: '' };
  const costChanged = costKeys.filter((op) => cost(op).method !== 'KEEP');
  const sellChanged = usable.filter((b) => sell(b.id).method !== 'KEEP');
  const count = costChanged.length + sellChanged.length;
  const valid = costChanged.every((op) => cost(op).method === 'END' || isDecimal(cost(op).amount)) && sellChanged.every((b) => sell(b.id).method === 'END' || isDecimal(sell(b.id).value));
  const errors = w.state.phase === 'error' ? rowErrors(w.state.error, sent) : {};
  const opLabel = (op: string | null) => (op ? e('operation', op) : t('tldPricing.price.everyOp'));

  // The cost an operation ends up with (fixed amounts only), for the "customer pays" preview.
  const costFor = (op: string): { amount: number; currency: string } | null => {
    for (const key of addon.operations.length > 1 ? [op, null] : [null]) {
      const c = cost(key);
      if (c.method === 'FIXED' && isDecimal(c.amount)) return { amount: Number(c.amount), currency: c.currency.trim().toUpperCase() };
      if (c.method === 'PERCENT_OF_REGISTRY_COST') return null;
      if (c.method === 'END') continue;
      const o = openCost(key);
      if (o) return o.amount_basis === 'FIXED' ? { amount: Number(o.amount), currency: o.currency } : null;
    }
    return null;
  };
  const preview = (b: PriceBook) => {
    const s = sell(b.id);
    const existing = openRule(b.id);
    const rule = s.method === 'END' ? null : s.method === 'KEEP' ? (existing ? { type: existing.rule_type, value: existing.value, years: existing.years, min_amount: existing.min_amount, max_amount: existing.max_amount } : null) : isDecimal(s.value) ? { type: s.method, value: s.value, years: null } : undefined;
    if (rule === undefined) return <span className="text-muted-foreground">—</span>;
    const ops = addon.operations.length ? addon.operations : [null];
    return (
      <ul className="grid gap-0.5 text-xs">
        {ops.map((op) => {
          const c = op ? costFor(op) : costFor('');
          const price = rule === null ? (c && c.currency === b.currency ? c.amount : null) : priceOf(rule, c && c.currency === b.currency ? String(c.amount) : null, 1);
          return (
            <li key={op ?? '*'} className="flex justify-between gap-2 whitespace-nowrap">
              {op ? <span className="text-muted-foreground">{e('operation', op)}</span> : null}
              {price === null ? <span className="text-muted-foreground">—</span> : <Amount amount={roundMoney(price, b.currency)} currency={b.currency} />}
            </li>
          );
        })}
      </ul>
    );
  };

  const submit = () => {
    const body: AddonPricesInput = {
      costs: costChanged.map((op) => {
        const c = cost(op);
        return c.method === 'END'
          ? { for_operation: op, action: 'END' }
          : { for_operation: op, action: 'SET', amount_basis: c.method === 'PERCENT_OF_REGISTRY_COST' ? 'PERCENT_OF_REGISTRY_COST' : 'FIXED', amount: c.amount.trim(), currency: c.currency.trim().toUpperCase() };
      }),
      rules: sellChanged.map((b) => {
        const s = sell(b.id);
        return s.method === 'END' ? { price_book_id: b.id, action: 'END' } : { price_book_id: b.id, action: 'SET', rule_type: s.method as Exclude<SellMethod, 'KEEP' | 'END'>, value: s.value.trim() };
      }),
      effective_from: from ? new Date(from).toISOString() : undefined,
      reason: reason.trim(),
    };
    setSent([...costChanged.map(ck), ...sellChanged.map((b) => `rule:${b.id}`)]);
    void w.run(body).then((r) => r && changed());
  };

  const costMethods: CostMethod[] = ['KEEP', 'FIXED', ...(opFee ? [] : (['PERCENT_OF_REGISTRY_COST'] as const)), 'END'];
  const sellMethods: SellMethod[] = ['KEEP', 'FIXED_PRICE', 'MARKUP_PERCENT', 'MARKUP_FIXED', 'END'];
  const costLabel = (m: CostMethod) => (m === 'KEEP' ? t('tldPricing.price.keep') : m === 'END' ? t('tldPricing.price.endCost') : tr(`tldPricing.price.bases.${m}`));
  const sellLabel = (m: SellMethod) => (m === 'KEEP' ? t('tldPricing.price.keep') : m === 'END' ? t('tldPricing.price.endRule') : e('ruleType', m));

  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        submit();
      }}
    >
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>{tr(`tldPricing.billing.${addon.billing_model}`)}</span>
        <span>{t('tldPricing.price.taxFixed', { tax: e('tax', addon.tax_category) })}</span>
        {addon.operations.length ? <span>{t('tldPricing.price.appliesTo', { ops: addon.operations.map((o) => e('operation', o)).join(', ') })}</span> : null}
      </div>
      {done ? (
        <Callout tone="ok">{t('tldPricing.price.savedCount', { count: sent.length })}</Callout>
      ) : (
        <>
          <section className="grid gap-2" aria-label={t('tldPricing.price.costs')}>
            <h3 className="text-sm font-medium">{t('tldPricing.price.costs')}</h3>
            <GridHeader alignLast={false} template={COST_ROWS} cols={[t('tldPricing.price.op'), t('tldPricing.price.current'), t('tldPricing.price.method'), t('tldPricing.price.amount'), t('tldPricing.price.currency')]} />
            {costKeys.map((op) => {
              const c = cost(op);
              const now = openCost(op);
              const label = opLabel(op);
              return (
                <GridRow key={op ?? '*'} template={COST_ROWS} error={errors[ck(op)]} label={`${t('tldPricing.price.costs')} · ${label}`}>
                  <span className="font-medium">{label}</span>
                  <GridCell label={t('tldPricing.price.current')}>
                    {now ? (
                      now.amount_basis === 'PERCENT_OF_REGISTRY_COST' ? (
                        t('tldPrices.percent', { value: now.amount })
                      ) : (
                        <Amount amount={now.amount} currency={now.currency} className="font-medium" />
                      )
                    ) : (
                      <span className="text-xs text-muted-foreground">{op ? t('tldPricing.price.inherit') : addon.free ? t('tldPricing.free') : t('tldPricing.price.none')}</span>
                    )}
                  </GridCell>
                  <GridCell label={t('tldPricing.price.method')}>
                    <select className={selectClass} value={c.method} onChange={(ev) => setCosts((x) => ({ ...x, [ck(op)]: { ...c, method: ev.target.value as CostMethod } }))} aria-label={`${label} · ${t('tldPricing.price.method')}`}>
                      {costMethods
                        .filter((m) => m !== 'END' || now)
                        .map((m) => (
                          <option key={m} value={m}>
                            {costLabel(m)}
                          </option>
                        ))}
                    </select>
                  </GridCell>
                  <GridCell label={c.method === 'PERCENT_OF_REGISTRY_COST' ? '%' : t('tldPricing.price.amount')}>
                    {c.method === 'FIXED' || c.method === 'PERCENT_OF_REGISTRY_COST' ? (
                      <Input
                        inputMode="decimal"
                        value={c.amount}
                        onChange={(ev) => setCosts((x) => ({ ...x, [ck(op)]: { ...c, amount: decimalInput(ev.target.value) } }))}
                        placeholder={c.method === 'PERCENT_OF_REGISTRY_COST' ? '%' : c.currency}
                        aria-label={`${label} · ${t('tldPricing.price.amount')}`}
                        required
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </GridCell>
                  <GridCell label={t('tldPricing.price.currency')}>
                    {c.method === 'FIXED' ? (
                      <Input value={c.currency} onChange={(ev) => setCosts((x) => ({ ...x, [ck(op)]: { ...c, currency: ev.target.value } }))} maxLength={3} className="w-20 uppercase" aria-label={`${label} · ${t('tldPricing.price.currency')}`} />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </GridCell>
                </GridRow>
              );
            })}
          </section>
          <section className="grid gap-2" aria-label={t('tldPricing.price.sells')}>
            <h3 className="text-sm font-medium">{t('tldPricing.price.sells')}</h3>
            <p className="text-xs text-muted-foreground">{t('tldPricing.price.sellsHint')}</p>
            <GridHeader template={SELL_ROWS} cols={[t('tldPricing.price.book'), t('tldPricing.price.current'), t('tldPricing.price.method'), t('tldPricing.price.value'), t('tldPricing.price.preview')]} />
            {usable.map((b) => {
              const s = sell(b.id);
              const now = openRule(b.id);
              return (
                <GridRow key={b.id} template={SELL_ROWS} error={errors[`rule:${b.id}`]} label={`${t('tldPricing.price.sells')} · ${b.code}`}>
                  <span className="font-mono text-xs font-medium">{b.code}</span>
                  <GridCell label={t('tldPricing.price.current')}>{now ? words(now, now.currency) : <span className="text-xs text-muted-foreground">{t('tldPricing.price.atCost')}</span>}</GridCell>
                  <GridCell label={t('tldPricing.price.method')}>
                    <select className={selectClass} value={s.method} onChange={(ev) => setSells((x) => ({ ...x, [b.id]: { ...s, method: ev.target.value as SellMethod } }))} aria-label={`${b.code} · ${t('tldPricing.price.method')}`}>
                      {sellMethods
                        .filter((m) => m !== 'END' || now)
                        .map((m) => (
                          <option key={m} value={m}>
                            {sellLabel(m)}
                          </option>
                        ))}
                    </select>
                  </GridCell>
                  <GridCell label={`${t('tldPricing.price.value')} (${s.method === 'MARKUP_PERCENT' ? '%' : b.currency})`}>
                    {s.method !== 'KEEP' && s.method !== 'END' ? (
                      <Input
                        inputMode="decimal"
                        value={s.value}
                        onChange={(ev) => setSells((x) => ({ ...x, [b.id]: { ...s, value: decimalInput(ev.target.value) } }))}
                        placeholder={s.method === 'MARKUP_PERCENT' ? '%' : b.currency}
                        aria-label={`${b.code} · ${t('tldPricing.price.value')}`}
                        required
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </GridCell>
                  <GridCell label={t('tldPricing.price.preview')} className="sm:text-right">
                    {preview(b)}
                  </GridCell>
                </GridRow>
              );
            })}
          </section>
          <Field label={t('common.effectiveFrom')} hint={t('common.effectiveFromHint')}>
            <Input type="datetime-local" value={from} onChange={(ev) => setFrom(ev.target.value)} className="w-60" />
          </Field>
          <ReasonField value={reason} onChange={setReason} />
          {count === 0 ? <p className="text-xs text-muted-foreground">{t('tldPricing.price.nothingGrid')}</p> : null}
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!count || !valid || !reason.trim()} submit={done ? undefined : count ? t('tldPricing.price.saveCount', { count }) : t('tldPricing.price.save')} onClose={close} />
    </form>
  );
}

function PriceButton({ tld, addon, books, onChanged }: { tld: TldRef; addon: TldAddon; books: PriceBook[]; onChanged: () => void }) {
  const { t } = useTranslation('services');
  const name = useLocalName();
  return (
    <ActionDialog
      label={t('tldPricing.editPrice')}
      icon={Pencil}
      variant="ghost"
      wide={GRID_DIALOG}
      title={t('tldPricing.price.title', {
        name: name(addon),
        zone: `.${tld.zone}`,
      })}
      description={t('tldPricing.price.gridDescription')}
      onChanged={onChanged}
    >
      {(p) => <PriceForm {...p} tld={tld} addon={addon} books={books} />}
    </ActionDialog>
  );
}

function ServiceName({ a, tld }: { a: TldAddon; tld: TldRef }) {
  const { t } = useTranslation('services');
  const tr = t as unknown as Lookup;
  const e = useEnum();
  const name = useLocalName();
  const differs = a.kind !== 'OPERATION_FEE' && (a.hint ?? null) !== (a.mode ?? null);
  const ops = a.operations.map((o) => e('operation', o)).join(', ');
  return (
    <div className="grid min-w-0 gap-0.5" title={[a.code, ops ? t('tldPricing.chargedWith', { ops }) : null].filter(Boolean).join(' · ')}>
      <span className="font-medium">{name(a)}</span>
      <span className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
        <span>{tr(`tldPricing.billing.${a.billing_model}`)}</span>·<span>{a.tax_category}</span>
        {!a.active ? <Badge tone="neutral">{t('catalogue.inactive')}</Badge> : null}
        {differs ? (
          <Badge
            tone="warn"
            icon={false}
            className="px-1.5 py-0 text-[11px]"
            title={t('tldPricing.differsTitle', {
              hint: a.hint ? tr(`tldPricing.mode.${a.hint}`) : t('tldPricing.noHint'),
            })}
          >
            {t('tldPricing.differs')}
          </Badge>
        ) : null}
      </span>
      <span className="sr-only">{`.${tld.zone}`}</span>
    </div>
  );
}

function FeesAndAddons({ tld, items, changes, source, books, onChanged }: { tld: TldRef; items: TldAddon[]; changes: TldAddonChange[]; source: string; books: PriceBook[]; onChanged: () => void }) {
  const { t } = useTranslation('services');
  const tr = t as unknown as Lookup;
  const e = useEnum();
  const name = useLocalName();
  const [pick, setPick] = useState<{
    addon: TldAddon;
    mode: ModeChoice;
  } | null>(null);
  const addons = items.filter((a) => a.kind !== 'OPERATION_FEE');
  const on = addons.filter((a) => a.mode);
  const off = addons.filter((a) => !a.mode);
  const fees = items.filter((a) => a.kind === 'OPERATION_FEE');
  const missing = on.filter((a) => a.missing_price);
  const cols: Column<TldAddon>[] = [
    {
      key: 'service',
      header: t('tldPricing.col.service'),
      cell: (a) => <ServiceName a={a} tld={tld} />,
    },
    {
      key: 'mode',
      header: t('tldPricing.col.mode'),
      cell: (a) => <ModeControl addon={a} onPick={(mode) => setPick({ addon: a, mode })} />,
    },
    {
      key: 'cost',
      header: t('tldPricing.col.cost'),
      cell: (a) => <CostCell a={a} />,
    },
    {
      key: 'sell',
      header: t('tldPricing.col.sell'),
      cell: (a) => <SellCell a={a} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (a) => <PriceButton tld={tld} addon={a} books={books} onChanged={onChanged} />,
    },
  ];
  const feeCols: Column<TldAddon>[] = [
    {
      key: 'service',
      header: t('tldPricing.col.service'),
      cell: (a) => (
        <div className="grid gap-0.5">
          <span className="font-medium">{name(a)}</span>
          <span className="text-xs text-muted-foreground">{t('tldPricing.feeFor', { case: e('ncCase', a.fee_for ?? a.code) })}</span>
        </div>
      ),
    },
    {
      key: 'mode',
      header: t('tldPricing.col.mode'),
      cell: (a) => (
        <ModeControl addon={a} choices={['OPTIONAL', 'OFF']} labelOf={(m) => (m === 'OFF' ? t('tldPricing.turnOff') : t('tldPricing.turnOn'))} onPick={(mode) => setPick({ addon: a, mode })} />
      ),
    },
    {
      key: 'cost',
      header: t('tldPricing.col.cost'),
      cell: (a) => <CostCell a={a} />,
    },
    {
      key: 'sell',
      header: t('tldPricing.col.sell'),
      cell: (a) => <SellCell a={a} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (a) => <PriceButton tld={tld} addon={a} books={books} onChanged={onChanged} />,
    },
  ];
  const changeCols: Column<TldAddonChange>[] = [
    {
      key: 'at',
      header: t('catalogue.col2.from'),
      className: 'whitespace-nowrap text-xs',
      cell: (c) => formatDateTime(c.created_at),
    },
    {
      key: 'code',
      header: t('tldPricing.col.service'),
      cell: (c) => <span className="font-mono text-xs">{c.code}</span>,
    },
    {
      key: 'mode',
      header: t('tldPricing.col.mode'),
      cell: (c) => (
        <span className="text-xs whitespace-nowrap">
          {tr(`tldPricing.mode.${c.mode_before ?? 'OFF'}`)} → {tr(`tldPricing.mode.${c.mode_after ?? 'OFF'}`)}
        </span>
      ),
    },
    {
      key: 'reason',
      header: t('common.reason'),
      cell: (c) => <span className="text-xs">{c.reason}</span>,
    },
  ];
  return (
    <>
      <Section title={t('tldPricing.fees')} description={t('tldPricing.feesHint', { file: source })} flush>
        {missing.length ? (
          <Callout tone="crit" className="mx-5 mb-4">
            {t('tldAddons.mandatoryMissing', { count: missing.length })}
          </Callout>
        ) : null}
        {on.length ? (
          <DataTable columns={cols} rows={on} rowKey={(a) => a.code} />
        ) : (
          <Callout tone="info" className="mx-5 mb-4">
            {t('tldPricing.noneOn')}
          </Callout>
        )}
        {off.length ? (
          <details className="border-t">
            <summary className="cursor-pointer px-5 py-3 text-sm font-medium text-muted-foreground hover:text-foreground">{t('tldPricing.offList', { count: off.length })}</summary>
            <ul className="divide-y border-t">
              {off.map((a) => (
                <li key={a.code} className="flex flex-wrap items-center justify-between gap-2 px-5 py-2">
                  <ServiceName a={a} tld={tld} />
                  <Button type="button" size="sm" variant="outline" onClick={() => setPick({ addon: a, mode: a.hint ?? 'OPTIONAL' })}>
                    {t('tldPricing.turnOn')}
                  </Button>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
        <details className="border-t">
          <summary className="cursor-pointer px-5 py-3 text-sm font-medium text-muted-foreground hover:text-foreground">{t('tldPricing.history', { count: changes.length })}</summary>
          <p className="px-5 pb-2 text-xs text-muted-foreground">{t('tldPricing.legacyNote')}</p>
          <DataTable columns={changeCols} rows={changes} rowKey={(c) => `${c.code}-${c.created_at}`} empty={<p className="text-sm text-muted-foreground">{t('tldAddons.noHistory')}</p>} />
        </details>
      </Section>
      {fees.length ? (
        <Section title={t('tldPricing.opFees')} description={t('tldPricing.opFeesHint')} flush>
          <DataTable columns={feeCols} rows={fees} rowKey={(a) => a.code} />
        </Section>
      ) : null}
      <ActionDialog
        label=""
        trigger={false}
        open={pick !== null}
        onOpenChange={(o) => !o && setPick(null)}
        title={
          pick
            ? t('tldPricing.modeTitle', {
                name: name(pick.addon),
                zone: `.${tld.zone}`,
              })
            : ''
        }
        onChanged={onChanged}
      >
        {(p) => (pick ? <ModeForm {...p} tld={tld} addon={pick.addon} mode={pick.mode} /> : null)}
      </ActionDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// The tab

/** `?tab=pricing` of a TLD: its prices and services, the only place they are edited. */
export function TldPricingPanel({ tld }: { tld: TldRef }) {
  const { t } = useTranslation('services');
  const e = useEnum();
  const addons = useApi((signal) => api.listTldAddons(tld.id, { signal }), [tld.id]);
  const pricing = useApi((signal) => api.getPricing({ history: false }, { signal }), []);
  const [tick, setTick] = useState(0);
  const reload = () => {
    addons.reload();
    pricing.reload();
    setTick((n) => n + 1);
  };
  const books = pricing.status === 'success' ? pricing.data.price_books : [];
  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader
        title={t('tldPricing.title', { zone: `.${tld.zone}` })}
        subtitle={t('tldPricing.subtitle')}
        actions={
          <>
            <Button type="button" variant="outline" size="sm" onClick={reload}>
              <RefreshCw /> {t('common.refresh')}
            </Button>
            <ReasonAction
              label={t('tldAddons.applyDefaults')}
              icon={ListChecks}
              title={t('tldAddons.applyDefaults')}
              description={t('tldAddons.applyDefaultsDescription', {
                cls: e('tldClass', tld.tld_class),
              })}
              run={(reason, opts) => api.applyTldAddonDefaults(tld.id, { reason }, opts)}
              done={(r) => (r.set.length ? t('tldAddons.appliedDefaults', { count: r.set.length }) : t('tldAddons.appliedNone'))}
              onChanged={reload}
            />
          </>
        }
      />
      {addons.status === 'error' ? <DataError error={addons.error} title={t('common.loadFailed')} onRetry={addons.reload} /> : null}
      {pricing.status === 'error' ? <DataError error={pricing.error} title={t('common.loadFailed')} onRetry={pricing.reload} /> : null}
      {addons.status === 'loading' || pricing.status === 'loading' ? <Loading rows={5} /> : null}
      {addons.status === 'success' && pricing.status === 'success' ? (
        <>
          {books.some((b) => b.status === 'ACTIVE') ? (
            <OperationSummary tld={tld} books={books} addons={addons.data.items} refreshKey={tick} />
          ) : (
            <Callout tone="warn">{t('tldPricing.noBooks')}</Callout>
          )}
          {/* Per-section editing for power users; open by itself when something needs fixing. */}
          <details
            className="group grid gap-6"
            open={!pricing.data.registry_prices.some((p) => p.tld_id === tld.id) || addons.data.items.some((a) => a.mode === 'MANDATORY' && a.missing_price) || undefined}
          >
            <summary className="cursor-pointer text-sm font-semibold text-primary">{t('tldPricing.advanced')}</summary>
            <div className="mt-4 flex min-w-0 flex-col gap-6">
              <RegistryCosts tld={tld} prices={pricing.data.registry_prices} onChanged={reload} />
              <FeesAndAddons tld={tld} items={addons.data.items} changes={addons.data.changes} source={addons.data.source} books={books} onChanged={reload} />
              <Section title={t('tldPricing.sell')} description={t('tldPricing.sellHint')} flush>
                <TldBookMatrix tldId={tld.id} zone={tld.zone} books={books} refreshKey={tick} />
              </Section>
            </div>
          </details>
        </>
      ) : null}
    </div>
  );
}
